import { memo } from 'react'

import { Button, Col, Row } from 'antd'
import { Form, Formik } from 'formik'
import { useToggle } from 'react-use'
import { Icon } from '@iconify/react'
import classNames from 'classnames'

import { useAuth } from 'app/providers/auth/AuthProvider'

import { FormControl } from 'common/components/FormItems/FormControl/FormControl'
import { FORM_CONTROL_TYPE } from 'common/constants/formControlConstants'
import { INPUT_TYPES } from 'common/constants/inputConstants'
import { Select } from 'common/components/Select/Select'
import { SELECT_CONSTANTS } from 'common/constants/selectConstants'
import { useApiResponse } from 'common/hooks/useApiResponse'

import {
  PATIENT_ACTIVITY_INTERVAL,
  PATIENT_CONFIRM_APPT_BEFORE,
} from 'features/Home/constants/infoActivityTime'
import { SaveButton } from 'features/Profile/components/SaveButton/SaveButton'
import { APPOINTMENT_COLOR_OPTIONS } from './constants/appointmentColorOptions'
import { SETTINGS_PROFILE_KEYS } from 'features/SettingsOld/constants/settingsProfileKeys'
import { SETTINGS_PROFILE_LABELS } from 'features/SettingsOld/constants/settingsProfileLabels'
import type { ProfileNotifications } from 'features/SettingsOld/interfaces/ISettings'
import { preferencesValidationSchema } from './schemas/preferencesValidationSchema'

import styles from './preferences.module.scss'
import {
  useFetchNotificationsQuery,
  useUpdateNotificationsMutation,
} from 'features/SettingsOld/state/api/settingsApi'
import { ColorConfigurator } from './ColorConfigurator/ColorConfigurator'
import { CategoryColorConfigurator } from './ColorConfigurator/CategoryColorConfigurator'

export const Preferences = memo(() => {
  const { isDoctor } = useAuth()
  const [openColorConfigurator, toggleOpenColorConfigurator] = useToggle(false)
  const [openCategoryColorConfigurator, toggleOpenCategoryColorConfigurator] = useToggle(false)
  const { processApiResponse } = useApiResponse()
  const { data, isFetching } = useFetchNotificationsQuery()
  const [updateNotifications] = useUpdateNotificationsMutation()

  const handleSubmit = async (values: ProfileNotifications) => {
    const _data = Object.entries(values).reduce((acc, [key, value]) => {
      acc[key] = Number(value)
      return acc
    }, {})

    const data = {
      ..._data,
      appointment_color: values.appointment_color,
    } as ProfileNotifications

    const response = await updateNotifications(data)
    processApiResponse(response, {
      error: 'Update notifications error',
      success: 'Update notifications success',
    })
  }

  if (!isDoctor) {
    return null
  }

  const {
    lease_notification,
    ticket_notification,
    patient_cancel_notification,
    patient_confirm_notification,
    patient_reschedule_notification,
    patient_checkin_notification,
    end_lease_notification,
    accept_early_patient_check_in,
    accept_walk_in_patient,
    default_appointment_time,
    allow_scribe,
    appointment_color,
    confirm_appt_before,
    cancel_policy_charge_days,
    right_away_appointment_reminder,
    first_appointment_reminder_before,
    patient_on_site_notification,
    recall_sms_before_days,
  } = data ?? {}

  return (
    <>
      <ColorConfigurator open={openColorConfigurator} onClose={toggleOpenColorConfigurator} />
      <CategoryColorConfigurator
        open={openCategoryColorConfigurator}
        onClose={toggleOpenCategoryColorConfigurator}
      />
      <Formik
        enableReinitialize
        validationSchema={preferencesValidationSchema}
        initialValues={{
          lease_notification,
          ticket_notification,
          patient_cancel_notification,
          patient_confirm_notification,
          patient_reschedule_notification,
          patient_checkin_notification,
          end_lease_notification,
          accept_early_patient_check_in,
          accept_walk_in_patient,
          default_appointment_time,
          allow_scribe,
          appointment_color,
          confirm_appt_before,
          cancel_policy_charge_days,
          right_away_appointment_reminder,
          first_appointment_reminder_before,
          patient_on_site_notification,
          recall_sms_before_days,
        }}
        onSubmit={handleSubmit}>
        {({ setFieldValue, isSubmitting, dirty, values, errors, touched }) => {
          return (
            <Form>
              <h1 className={styles.sectionTitle}>Notifications</h1>
              <Row gutter={[32, 32]}>
                <Col xs={24} md={12}>
                  <h2 className={styles.containerName}>
                    {SETTINGS_PROFILE_LABELS.SCHEDULE_NOTIFICATIONS}
                  </h2>
                  <p className={styles.containerLabel}>
                    {SETTINGS_PROFILE_LABELS.SCHEDULE_NOTIFICATIONS_LABEL}
                  </p>
                  <Row gutter={[16, 16]}>
                    <Col xs={24}>
                      <FormControl
                        isLoading={isFetching}
                        type={INPUT_TYPES.TEXT}
                        control={FORM_CONTROL_TYPE.SWITCH}
                        name={SETTINGS_PROFILE_KEYS.PATIENT_CONFIRMATION}
                        label={SETTINGS_PROFILE_LABELS.PATIENT_CONFIRMATION}
                      />
                    </Col>
                    <Col xs={24}>
                      <FormControl
                        isLoading={isFetching}
                        type={INPUT_TYPES.TEXT}
                        control={FORM_CONTROL_TYPE.SWITCH}
                        name={SETTINGS_PROFILE_KEYS.PATIENT_CANCELATION}
                        label={SETTINGS_PROFILE_LABELS.PATIENT_CANCELATION}
                      />
                    </Col>
                    <Col xs={24}>
                      <FormControl
                        isLoading={isFetching}
                        type={INPUT_TYPES.TEXT}
                        control={FORM_CONTROL_TYPE.SWITCH}
                        name={SETTINGS_PROFILE_KEYS.PATIENT_RESCHEDULE}
                        label={SETTINGS_PROFILE_LABELS.PATIENT_RESCHEDULE}
                      />
                    </Col>
                    <Col xs={24}>
                      <FormControl
                        isLoading={isFetching}
                        type={INPUT_TYPES.TEXT}
                        control={FORM_CONTROL_TYPE.SWITCH}
                        name={SETTINGS_PROFILE_KEYS.PATIENT_CHECKIN}
                        label={SETTINGS_PROFILE_LABELS.PATIENT_CHECKIN}
                      />
                    </Col>
                    <Col xs={24}>
                      <FormControl
                        isLoading={isFetching}
                        type={INPUT_TYPES.TEXT}
                        control={FORM_CONTROL_TYPE.SWITCH}
                        name={SETTINGS_PROFILE_KEYS.END_OF_BOOKING}
                        label={SETTINGS_PROFILE_LABELS.END_OF_BOOKING}
                      />
                    </Col>
                    <Col xs={24}>
                      <FormControl
                        isLoading={isFetching}
                        type={INPUT_TYPES.TEXT}
                        control={FORM_CONTROL_TYPE.SWITCH}
                        name={SETTINGS_PROFILE_KEYS.PATIENT_ON_SITE_TEXT_NOTIFICATION}
                        label={SETTINGS_PROFILE_LABELS.PATIENT_ON_SITE_TEXT_MESSAGES}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col xs={24} md={12}>
                  <h2 className={styles.containerName}>
                    {SETTINGS_PROFILE_LABELS.BOOKING_NOTIFICATIONS}
                  </h2>
                  <p className={styles.containerLabel}>
                    {SETTINGS_PROFILE_LABELS.EMAIL_NOTIFICATIONS_LABEL}
                  </p>
                  <Row gutter={[16, 16]}>
                    <Col xs={24}>
                      <FormControl
                        isLoading={isFetching}
                        type={INPUT_TYPES.TEXT}
                        control={FORM_CONTROL_TYPE.SWITCH}
                        name={SETTINGS_PROFILE_KEYS.LEASE_NOTIFICATION}
                        label={SETTINGS_PROFILE_LABELS.RELATED_NOTIFICATIONS}
                      />
                    </Col>
                    <Col xs={24}>
                      <FormControl
                        isLoading={isFetching}
                        type={INPUT_TYPES.TEXT}
                        control={FORM_CONTROL_TYPE.SWITCH}
                        name={SETTINGS_PROFILE_KEYS.TICKET_NOTIFICATION}
                        label={SETTINGS_PROFILE_LABELS.STATUS_NOTIFICATIONS}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col xs={24} md={12}>
                  <h1 className={styles.sectionTitle}>Schedule</h1>
                  <Row gutter={[16, 16]}>
                    <Col xs={24}>
                      <FormControl
                        isLoading={isFetching}
                        type={INPUT_TYPES.TEXT}
                        control={FORM_CONTROL_TYPE.SWITCH}
                        name={SETTINGS_PROFILE_KEYS.ACCEPT_WALK_IN_PATIENT}
                        label={SETTINGS_PROFILE_LABELS.ACCEPT_WALK_IN_PATIENT}
                      />
                    </Col>
                    <Col xs={24}>
                      <FormControl
                        isLoading={isFetching}
                        type={INPUT_TYPES.TEXT}
                        control={FORM_CONTROL_TYPE.SWITCH}
                        name={SETTINGS_PROFILE_KEYS.ALLOW_SCRIBE}
                        label={SETTINGS_PROFILE_LABELS.ALLOW_SCRIBE}
                      />
                    </Col>
                    {/* <Col xs={24}>
                  <FormControl
                    isLoading={isFetching}
                    type={INPUT_TYPES.TEXT}
                    control={FORM_CONTROL_TYPE.SWITCH}
                    name={SETTINGS_PROFILE_KEYS.ACCEPT_EARLY_PATIENT_CHECK_IN}
                    label={SETTINGS_PROFILE_LABELS.ACCEPT_EARLY_PATIENT_CHECK_IN}
                  />
                </Col> */}

                    <Col xs={24}>
                      <FormControl
                        isLoading={isFetching}
                        type={INPUT_TYPES.TEXT}
                        control={FORM_CONTROL_TYPE.SWITCH}
                        name={SETTINGS_PROFILE_KEYS.RIGHT_AWAY_APPOINTMENT_REMINDER}
                        label={SETTINGS_PROFILE_LABELS.RIGHT_AWAY_APPOINTMENT_REMINDER}
                      />
                    </Col>
                    <Col xs={24}>
                      <div
                        className={classNames(
                          styles.defaultAppointmentIntervalContainer,
                          styles.firstAppointmentReminderBefore,
                        )}>
                        <label className={styles.label}>
                          {SETTINGS_PROFILE_LABELS.FIRST_APPOINTMENT_REMINDER_BEFORE}
                        </label>
                        <FormControl
                          isLoading={isFetching}
                          type={INPUT_TYPES.NUMBER}
                          control={FORM_CONTROL_TYPE.INPUT}
                          name={SETTINGS_PROFILE_KEYS.FIRST_APPOINTMENT_REMINDER_BEFORE}
                          disabled={Boolean(values.right_away_appointment_reminder)}
                          min={1}
                        />
                      </div>
                    </Col>

                    <Col xs={24}>
                      <div className={styles.defaultAppointmentIntervalContainer}>
                        <label className={styles.label}>
                          {SETTINGS_PROFILE_LABELS.CONFIRM_APPT_BEFORE}
                        </label>
                        <div>
                          <Select
                            skeletonClassName={styles.skeleton}
                            isLoading={isFetching}
                            onSelect={(value) =>
                              setFieldValue(SETTINGS_PROFILE_KEYS.CONFIRM_APPT_BEFORE, value)
                            }
                            className={styles.defaultAppointmentInterval}
                            proportion={SELECT_CONSTANTS.SMALL}
                            listOptions={PATIENT_CONFIRM_APPT_BEFORE}
                            value={String(values.confirm_appt_before)}
                          />
                        </div>
                      </div>
                      {touched.confirm_appt_before && errors.confirm_appt_before && (
                        <p className='errorMessage'>{errors.confirm_appt_before}</p>
                      )}
                    </Col>

                    <Col xs={24}>
                      <div className={styles.defaultAppointmentIntervalContainer}>
                        <label className={styles.label}>
                          {SETTINGS_PROFILE_LABELS.CANCEL_POLICY_CHARGE_DAYS}
                        </label>
                        <div>
                          <Select
                            skeletonClassName={styles.skeleton}
                            isLoading={isFetching}
                            onSelect={(value) =>
                              setFieldValue(SETTINGS_PROFILE_KEYS.CANCEL_POLICY_CHARGE_DAYS, value)
                            }
                            className={styles.defaultAppointmentInterval}
                            proportion={SELECT_CONSTANTS.SMALL}
                            listOptions={PATIENT_CONFIRM_APPT_BEFORE}
                            value={String(values.cancel_policy_charge_days)}
                          />
                        </div>
                      </div>
                      {touched.cancel_policy_charge_days && errors.cancel_policy_charge_days && (
                        <p className='errorMessage'>{errors.cancel_policy_charge_days}</p>
                      )}
                    </Col>
                    <Col xs={24}>
                      <div className={styles.defaultAppointmentIntervalContainer}>
                        <label className={styles.label}>
                          {SETTINGS_PROFILE_LABELS.DEFAULT_APPOINTMENT_TIME}
                        </label>
                        <div>
                          <Select
                            skeletonClassName={styles.skeleton}
                            isLoading={isFetching}
                            onSelect={(value) =>
                              setFieldValue(SETTINGS_PROFILE_KEYS.DEFAULT_APPOINTMENT_TIME, value)
                            }
                            className={styles.defaultAppointmentInterval}
                            proportion={SELECT_CONSTANTS.SMALL}
                            listOptions={PATIENT_ACTIVITY_INTERVAL}
                            value={values.default_appointment_time}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col xs={24}>
                      <div className={styles.defaultAppointmentIntervalContainer}>
                        <label className={styles.label}>
                          {SETTINGS_PROFILE_LABELS.APPOINTMENT_COLOR}
                        </label>
                        <div>
                          <Select
                            skeletonClassName={styles.skeleton}
                            isLoading={isFetching}
                            onSelect={(value) =>
                              setFieldValue(SETTINGS_PROFILE_KEYS.APPOINTMENT_COLOR, value)
                            }
                            className={styles.appointmentColorSelect}
                            proportion={SELECT_CONSTANTS.SMALL}
                            listOptions={APPOINTMENT_COLOR_OPTIONS}
                            value={values.appointment_color}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col xs={24}>
                      <div
                        className={classNames(
                          styles.defaultAppointmentIntervalContainer,
                          styles.firstAppointmentReminderBefore,
                        )}>
                        <label className={styles.label}>
                          {SETTINGS_PROFILE_LABELS.SEND_SMS_RECALL_BEFORE_APPOINTMENT}
                        </label>
                        <FormControl
                          isLoading={isFetching}
                          type={INPUT_TYPES.NUMBER}
                          control={FORM_CONTROL_TYPE.INPUT}
                          name={SETTINGS_PROFILE_KEYS.RECALL_SMS_BEFORE_DAYS}
                          min={0}
                        />
                      </div>
                    </Col>
                    <Col xs={24}>
                      <div className={styles.defaultAppointmentIntervalContainer}>
                        <span className={styles.label}>Customize Status Colors</span>
                        <div>
                          <Button
                            className={styles.colorConfiguratorButton}
                            htmlType='button'
                            size='small'
                            iconPosition='end'
                            variant='filled'
                            onClick={toggleOpenColorConfigurator}
                            icon={
                              <Icon icon='ant-design:export-outlined' width={16} height={16} />
                            }>
                            Open Configurator
                          </Button>
                        </div>
                      </div>
                    </Col>
                    <Col xs={24}>
                      <div className={styles.defaultAppointmentIntervalContainer}>
                        <span className={styles.label}>Customize Category Colors</span>
                        <div>
                          <Button
                            className={styles.colorConfiguratorButton}
                            htmlType='button'
                            size='small'
                            iconPosition='end'
                            variant='filled'
                            onClick={toggleOpenCategoryColorConfigurator}
                            icon={
                              <Icon icon='ant-design:export-outlined' width={16} height={16} />
                            }>
                            Open Configurator
                          </Button>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Row>
                <SaveButton
                  disabled={!dirty}
                  className={styles.formSubmit}
                  loading={isFetching || isSubmitting}
                />
              </Row>
            </Form>
          )
        }}
      </Formik>
    </>
  )
})
